import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
  Card, CardHeader, CardBody, MetricCard, InsightCard,
  DataTable, Chart, DashboardSkeleton, PageError, SampleDataBanner,
} from "../components/index.js";
import { useDashboardData } from "../hooks/useDashboardData.js";
import { rangeDays } from "../lib/time.js";

const PAGE_SIZE = 25;
const AXIS_STYLE = { fontSize: 10, fill: "#a3a3a3" };
const CLICKS_COLOR = "#ff6804";
const IMPRESSIONS_COLOR = "#737373";

export function Seo() {
  const { data, loading, error, refetch } = useDashboardData("seo");
  const [searchParams] = useSearchParams();
  const days = rangeDays(searchParams.get("from"), searchParams.get("to"));

  if (loading) return <DashboardSkeleton />;
  if (error)   return <PageError error={error} onRetry={refetch} />;
  if (!data)   return null;

  const { metrics, topQueries, topPages, dailyTrend, positionBuckets, deviceBreakdown, insights, period } = data;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {period?.hasRealData === false && <SampleDataBanner />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard {...metrics.clicks} />
        <MetricCard {...metrics.impressions} />
        <MetricCard {...metrics.avgPosition} />
        <MetricCard {...metrics.avgCtr} />
      </div>

      <Card>
        <CardHeader title="Clicks & Impressions" subtitle={`Daily · last ${days} days`} />
        <CardBody>
          <TrendChart data={dailyTrend} />
        </CardBody>
      </Card>

      {insights?.[0] && <InsightCard>{insights[0].content}</InsightCard>}

      <TopQueries rows={topQueries} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Top Pages" subtitle="By clicks" />
          <DataTable
            columns={[
              { key: "page", label: "Page", render: (r) => <span className="font-mono text-[11px]">{r.page}</span> },
              { key: "clicks", label: "Clicks", align: "right", render: (r) => r.clicks.toLocaleString() },
              { key: "impressions", label: "Impr.", align: "right", render: (r) => r.impressions.toLocaleString() },
              { key: "ctr", label: "CTR", align: "right", render: (r) => `${r.ctr}%` },
            ]}
            rows={topPages.slice(0, 25)}
          />
        </Card>

        <Card>
          <CardHeader title="Position Distribution" subtitle="Queries by average rank" />
          <CardBody>
            <Chart
              type="bar"
              data={positionBuckets}
              xKey="range"
              series={[{ key: "count", color: CLICKS_COLOR, label: "Queries" }]}
              height={220}
            />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Device Breakdown" subtitle="Clicks by device" />
        <CardBody>
          <DeviceChips devices={deviceBreakdown} />
        </CardBody>
      </Card>
    </div>
  );
}

function TrendChart({ data }) {
  if (!data?.length) {
    return <div className="py-10 text-center text-[13px] text-text-muted">No trend data for this period.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="#f0f0f0" vertical={false} />
        <XAxis dataKey="date" tick={AXIS_STYLE} axisLine={false} tickLine={false}
          interval="preserveStartEnd" minTickGap={24}
          tickFormatter={(d) => { const x = new Date(d); return `${x.getUTCMonth() + 1}/${x.getUTCDate()}`; }} />
        {/* Dual axis: clicks (left) and impressions (right) live on very
            different scales, so a shared axis would flatten the clicks line. */}
        <YAxis yAxisId="clicks" tick={AXIS_STYLE} axisLine={false} tickLine={false} width={40} />
        <YAxis yAxisId="impressions" orientation="right" tick={AXIS_STYLE} axisLine={false} tickLine={false} width={48} />
        <Tooltip
          contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e5e5", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
          formatter={(v) => v.toLocaleString()}
          labelFormatter={(d) => { const x = new Date(d); return `${x.getUTCMonth() + 1}/${x.getUTCDate()}`; }}
        />
        <Line yAxisId="clicks" type="monotone" dataKey="clicks" name="Clicks" stroke={CLICKS_COLOR} strokeWidth={2} dot={false} activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} />
        <Line yAxisId="impressions" type="monotone" dataKey="impressions" name="Impressions" stroke={IMPRESSIONS_COLOR} strokeWidth={2} dot={false} strokeDasharray="4 3" />
      </LineChart>
    </ResponsiveContainer>
  );
}

const QUERY_COLS = [
  { key: "query", label: "Query", numeric: false },
  { key: "clicks", label: "Clicks", numeric: true },
  { key: "impressions", label: "Impressions", numeric: true },
  { key: "ctr", label: "CTR", numeric: true },
  { key: "position", label: "Avg Position", numeric: true },
];

function TopQueries({ rows }) {
  const [sortKey, setSortKey] = useState("clicks");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const slice = sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  function toggleSort(key) {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "query" ? "asc" : "desc"); }
    setPage(0);
  }

  return (
    <Card>
      <CardHeader title="Top Queries" subtitle={`${(rows || []).length} queries · sortable`} />
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] border-collapse" style={{ fontFeatureSettings: "'tnum'" }}>
          <thead>
            <tr>
              {QUERY_COLS.map((c) => (
                <th key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className={`text-[11px] uppercase text-text-muted font-medium px-4 py-2.5 border-b border-border-light cursor-pointer select-none hover:text-text-secondary ${c.numeric ? "text-right" : "text-left"}`}
                  style={{ letterSpacing: "0.5px" }}>
                  {c.label}
                  {sortKey === c.key && <span className="ml-1 text-accent">{sortDir === "asc" ? "▲" : "▼"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.map((r, i) => (
              <tr key={i} className="hover:bg-[var(--color-border-light)]/40">
                <td className="px-4 py-2.5 text-text-primary border-b border-border-light">{r.query}</td>
                <td className="px-4 py-2.5 text-text-secondary border-b border-border-light text-right">{r.clicks.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-text-secondary border-b border-border-light text-right">{r.impressions.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-text-secondary border-b border-border-light text-right">{r.ctr}%</td>
                <td className="px-4 py-2.5 text-text-secondary border-b border-border-light text-right">{r.position}</td>
              </tr>
            ))}
            {slice.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-text-muted">No query data for this period.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border-light text-[12px] text-text-muted">
          <span>Page {safePage + 1} of {pageCount}</span>
          <span className="flex gap-2">
            <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}
              className="h-7 px-2.5 rounded border border-border-default disabled:opacity-40 hover:bg-[var(--color-border-light)]">Prev</button>
            <button type="button" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}
              className="h-7 px-2.5 rounded border border-border-default disabled:opacity-40 hover:bg-[var(--color-border-light)]">Next</button>
          </span>
        </div>
      )}
    </Card>
  );
}

function DeviceChips({ devices }) {
  if (!devices?.length) {
    return <div className="text-[13px] text-text-muted">No device data for this period.</div>;
  }
  const total = devices.reduce((a, d) => a + (d.clicks || 0), 0) || 1;
  const LABELS = { DESKTOP: "Desktop", MOBILE: "Mobile", TABLET: "Tablet" };
  return (
    <div className="flex flex-wrap gap-3">
      {devices.map((d) => {
        const pct = Math.round((d.clicks / total) * 100);
        return (
          <div key={d.device} className="flex items-center gap-2.5 rounded-card border border-border-default px-4 py-2.5">
            <span className="text-[13px] font-medium text-text-primary">{LABELS[d.device] || d.device}</span>
            <span className="text-[13px] text-text-secondary">{d.clicks.toLocaleString()}</span>
            <span className="text-[11px] text-text-muted">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}
